import type { StaffTeamConflictCode } from "@lafayette/schemas";

import { describeStaffFailure, neutralForbidden } from "../failure-words";

// How the team, coverage and support screens tell a staff member what went wrong. Rules:
//   1. No id, key, name, reason or provider text goes into a message. The API sends none either: a refusal holds a code.
//   2. A refusal for access (403) and a missing id (404) read the same: the page never says whether a person or a customer exists.
//   3. A stale screen (`staff.stale_view`, `assignment.stale_view`, `availability.stale_view`) says nothing was changed and that the page shows the latest.
//   4. A failure that may have run says so, and whether the SAME action can be sent again (the same Idempotency-Key makes the server run it once).

export type TeamFailureKind =
  | "stale"
  | "conflict"
  | "invalid"
  | "bad_request"
  | "forbidden"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type TeamFailure = Readonly<{
  kind: TeamFailureKind;
  message: string;
  /** The same action may be sent again with the SAME idempotency key: it may or may not have run. */
  retryable: boolean;
  /** The page must read again: what the person saw is out of date. */
  reload: boolean;
  code: StaffTeamConflictCode | null;
}>;

const NEUTRAL_TEAM_FORBIDDEN = neutralForbidden("this", "an administrator or a supervisor");

export const conflictMessages: Readonly<Record<StaffTeamConflictCode, string>> = {
  "staff.stale_view":
    "This person's access changed while you were looking. Nothing was changed. The page shows the latest.",
  "assignment.stale_view":
    "The cover of this customer changed while you were looking. Nothing was changed. The page shows the latest.",
  "availability.stale_view":
    "The unavailability windows of this person changed while you were looking. Nothing was changed. The page shows the latest.",
  "staff.role_already_granted": "This person already has this role. Nothing was changed.",
  "staff.role_not_granted": "This person does not have this role. Nothing was changed.",
  "staff.already_deactivated": "This person is already offboarded. Nothing was changed.",
  "staff.not_deactivated": "This person is not offboarded. Nothing was changed.",
  "staff.last_administrator":
    "This is the only administrator. Give the role to another person first. Nothing was changed.",
  "assignment.primary_already_active":
    "This customer already has a primary concierge. Use Replace primary. Nothing was changed.",
  "assignment.no_active_primary":
    "This customer has no primary concierge to replace. Assign a primary instead. Nothing was changed.",
  "assignment.same_concierge": "This person is already the primary concierge. Nothing was changed.",
  "assignment.concierge_already_assigned":
    "This person already covers this customer. Nothing was changed.",
  "assignment.backup_already_active":
    "This customer already has a backup. End it first, or replace the primary and keep the old one as backup. Nothing was changed.",
  "assignment.temporary_already_active":
    "This customer already has temporary cover. End it first. Nothing was changed.",
  "assignment.not_active": "This assignment has already ended. Nothing was changed.",
  "availability.window_not_open": "This window has already ended or run out. Nothing was changed.",
};

export function describeTeamError(error: unknown): TeamFailure {
  const { api, kind, message, retryable, reload } = describeStaffFailure(error, {
    forbidden: NEUTRAL_TEAM_FORBIDDEN,
    conflict: ({ detail }) =>
      detail.teamConflict === null
        ? "This conflicts with the current state, or the same action is still running. Nothing was changed by this attempt. The page shows the latest."
        : conflictMessages[detail.teamConflict.code],
    unavailable:
      "The service is not available right now. Try again in a moment: the same action is never applied twice.",
  });
  const code = kind === "conflict" ? (api.detail.teamConflict?.code ?? null) : null;
  return {
    kind: kind === "conflict" && api.staleView ? "stale" : kind,
    message,
    retryable,
    reload,
    code,
  };
}
